import React, { useState } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ToolsAccessoriesChecklist = ({ checklistData, onChange }) => {
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [uploadingPhotos, setUploadingPhotos] = useState({});

  const categories = [
    {
      id: 'keys',
      label: 'Llaves y Controles',
      icon: 'Key',
      items: [
        { id: 'mainKey', label: 'Llave Principal' },
        { id: 'spareKey', label: 'Llave de Repuesto' },
        { id: 'remoteControl', label: 'Control Remoto' },
        { id: 'keyChain', label: 'Llavero' }
      ]
    },
    {
      id: 'spare',
      label: 'Refacciones y Herramientas',
      icon: 'Wrench',
      items: [
        { id: 'spareTire', label: 'Llanta de Refacción' },
        { id: 'jack', label: 'Gato Hidráulico' },
        { id: 'wrench', label: 'Llave de Cruz' },
        { id: 'toolKit', label: 'Kit de Herramientas' },
        { id: 'triangles', label: 'Triángulos de Seguridad' }
      ]
    },
    {
      id: 'documentation',
      label: 'Documentación',
      icon: 'FileText',
      items: [
        { id: 'ownerManual', label: 'Manual del Propietario' },
        { id: 'serviceBook', label: 'Libro de Servicio' },
        { id: 'warranty', label: 'Garantía' },
        { id: 'insurance', label: 'Póliza de Seguro' }
      ]
    },
    {
      id: 'personal',
      label: 'Artículos Personales',
      icon: 'Briefcase',
      items: [
        { id: 'sunglasses', label: 'Lentes de Sol' },
        { id: 'phone', label: 'Teléfono/Cargador' },
        { id: 'documents', label: 'Documentos Personales' },
        { id: 'other', label: 'Otros' }
      ]
    },
    {
      id: 'electronics',
      label: 'Electrónicos',
      icon: 'Smartphone',
      items: [
        { id: 'gps', label: 'GPS' },
        { id: 'dashcam', label: 'Cámara de Tablero' },
        { id: 'usbCables', label: 'Cables USB' },
        { id: 'audioSystem', label: 'Sistema de Audio' }
      ]
    }
  ];

  const handleItemToggle = (categoryId, itemId, itemLabel) => {
    const updatedChecklist = { ...checklistData };
    if (!updatedChecklist?.[categoryId]) {
      updatedChecklist[categoryId] = {};
    }

    if (updatedChecklist?.[categoryId]?.[itemId]) {
      delete updatedChecklist?.[categoryId]?.[itemId];
    } else {
      updatedChecklist[categoryId][itemId] = { 
        checked: true, 
        quantity: 1,
        itemName: itemLabel,
        photos: []
      };
    }

    onChange(updatedChecklist);
  };

  const handleQuantityChange = (categoryId, itemId, quantity) => {
    const updatedChecklist = { ...checklistData };
    if (updatedChecklist?.[categoryId]?.[itemId]) {
      updatedChecklist[categoryId][itemId].quantity = parseInt(quantity) || 1;
      onChange(updatedChecklist);
    }
  };

  const handlePhotoUpload = async (categoryId, itemId, event) => {
    const files = Array.from(event?.target?.files || []);
    if (files?.length === 0) return;

    const photoKey = `${categoryId}-${itemId}`;
    setUploadingPhotos(prev => ({ ...prev, [photoKey]: true }));

    try {
      const updatedChecklist = { ...checklistData };
      if (!updatedChecklist?.[categoryId]?.[itemId]) return;

      // Store File objects temporarily (will be uploaded on form submit)
      const existingPhotos = updatedChecklist?.[categoryId]?.[itemId]?.photos || [];
      updatedChecklist[categoryId][itemId].photos = [...existingPhotos, ...files];

      onChange(updatedChecklist);
    } catch (error) {
      console.error('Error handling photo upload:', error);
      alert('Error al cargar la foto. Por favor intente nuevamente.');
    } finally {
      setUploadingPhotos(prev => ({ ...prev, [photoKey]: false }));
    }
  };

  const handleRemovePhoto = (categoryId, itemId, photoIndex) => {
    const updatedChecklist = { ...checklistData };
    if (updatedChecklist?.[categoryId]?.[itemId]?.photos) {
      updatedChecklist[categoryId][itemId].photos = 
        updatedChecklist?.[categoryId]?.[itemId]?.photos?.filter((_, idx) => idx !== photoIndex);
      onChange(updatedChecklist);
    }
  };

  const getCategoryCount = (categoryId) => {
    return Object.keys(checklistData?.[categoryId] || {})?.length;
  };

  const getPhotoCount = (categoryId, itemId) => {
    return checklistData?.[categoryId]?.[itemId]?.photos?.length || 0;
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-2 p-4 md:p-6">
      <div className="flex items-center gap-2 mb-4 md:mb-6">
        <div className="p-2 rounded-lg bg-accent/10">
          <Icon name="ClipboardCheck" size={24} className="text-accent" />
        </div>
        <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
          Herramientas y Accesorios
        </h2>
      </div>
      <div className="space-y-3">
        {categories?.map((category) => {
          const isExpanded = expandedCategory === category?.id;
          const itemCount = getCategoryCount(category?.id);

          return (
            <div
              key={category?.id}
              className="border border-border rounded-lg bg-muted/30 transition-smooth"
            >
              <button
                type="button"
                onClick={() => setExpandedCategory(isExpanded ? null : category?.id)}
                className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-smooth"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Icon name={category?.icon} size={20} className="text-primary" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-base font-heading font-semibold text-foreground">
                      {category?.label}
                    </h3>
                    {itemCount > 0 && (
                      <p className="text-sm caption text-muted-foreground">
                        {itemCount} artículo(s) registrado(s)
                      </p>
                    )}
                  </div>
                </div>
                <Icon 
                  name={isExpanded ? 'ChevronUp' : 'ChevronDown'} 
                  size={20} 
                  className="text-muted-foreground"
                />
              </button>
              {isExpanded && (
                <div className="p-4 border-t border-border space-y-4">
                  {category?.items?.map((item) => {
                    const isChecked = checklistData?.[category?.id]?.[item?.id]?.checked || false;
                    const quantity = checklistData?.[category?.id]?.[item?.id]?.quantity || 1;
                    const photoCount = getPhotoCount(category?.id, item?.id);
                    const photoKey = `${category?.id}-${item?.id}`;
                    const isUploading = uploadingPhotos?.[photoKey];

                    return (
                      <div key={item?.id} className="space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <Checkbox
                              label={item?.label}
                              checked={isChecked}
                              onChange={() => handleItemToggle(category?.id, item?.id, item?.label)}
                            />
                          </div>
                          {isChecked && (
                            <div className="w-20">
                              <Input
                                type="number"
                                min={1}
                                value={quantity}
                                onChange={(e) => handleQuantityChange(category?.id, item?.id, e?.target?.value)}
                                className="text-center"
                                placeholder="Cant."
                              />
                            </div>
                          )}
                        </div>
                        {isChecked && (
                          <div className="ml-6 space-y-2">
                            <div className="flex items-center gap-2">
                              <label className="relative">
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  onChange={(e) => handlePhotoUpload(category?.id, item?.id, e)}
                                  className="hidden"
                                  disabled={isUploading}
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  iconName="Camera"
                                  iconPosition="left"
                                  disabled={isUploading}
                                  onClick={(e) => {
                                    e?.preventDefault();
                                    e?.currentTarget?.previousElementSibling?.click();
                                  }}
                                >
                                  {isUploading ? 'Cargando...' : 'Agregar Foto'}
                                </Button>
                              </label>
                              {photoCount > 0 && (
                                <span className="text-sm text-muted-foreground">
                                  {photoCount} foto(s)
                                </span>
                              )}
                            </div>
                            {photoCount > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {checklistData?.[category?.id]?.[item?.id]?.photos?.map((photo, idx) => (
                                  <div key={idx} className="relative group">
                                    <img
                                      src={URL.createObjectURL(photo)}
                                      alt={`${item?.label} - Foto ${idx + 1}`}
                                      className="w-20 h-20 object-cover rounded-lg border border-border"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => handleRemovePhoto(category?.id, item?.id, idx)}
                                      className="absolute -top-2 -right-2 p-1 rounded-full bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 transition-smooth"
                                    >
                                      <Icon name="X" size={14} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-4 p-4 rounded-lg bg-muted/50 border border-border">
        <div className="flex items-start gap-2">
          <Icon name="Info" size={16} className="text-muted-foreground mt-0.5" />
          <p className="text-sm caption text-muted-foreground">
            Registre todos los artículos que el cliente entrega con el vehículo y agregue fotos como evidencia. Esta información será verificada al momento de la entrega.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ToolsAccessoriesChecklist;